import { useMemo, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea
} from "@/lib/charts/leaf";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown } from "lucide-react";
import type { TooltipProps } from "@/lib/charts/leaf";
type ChartDatum = {
  month: string;
  cumOnPremTcoUSD: number;
  cumCloudUSD: number;
  monthIndex: number;
};

// Accept either full MonthlyRollup or simplified chart data
interface ChartDataPoint {
  monthKey: string;
  cumOnPremTcoUSD: number;
  cumCloudUSD: number;
}

interface EnhancedChartProps {
  monthlyRollups: ChartDataPoint[];
  className?: string;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { 
    style: "currency", 
    currency: "USD",
    notation: value > 1000000 ? "compact" : "standard",
    maximumFractionDigits: 0
  }).format(value);

export const EnhancedChart = ({ monthlyRollups, className = "" }: EnhancedChartProps) => {
  const chartRef = useRef<HTMLDivElement>(null);

  const chartData = useMemo<ChartDatum[]>(
    () =>
      monthlyRollups.map((month, index) => ({
        month: month.monthKey,
        cumOnPremTcoUSD: month.cumOnPremTcoUSD,
        cumCloudUSD: month.cumCloudUSD,
        monthIndex: index
      })),
    [monthlyRollups]
  );

  // Find crossover point
  const crossoverPoint = useMemo(() => {
    for (let i = 0; i < chartData.length; i++) {
      const point = chartData[i];
      if (point.cumOnPremTcoUSD <= point.cumCloudUSD) {
        return {
          month: point.month,
          monthIndex: i,
          onPremCost: point.cumOnPremTcoUSD,
          cloudCost: point.cumCloudUSD,
          savings: point.cumCloudUSD - point.cumOnPremTcoUSD
        };
      }
    }
    return null;
  }, [chartData]);

  // Animate chart on mount
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.style.opacity = '0';
      chartRef.current.style.transform = 'translateY(20px)';
      
      setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.style.transition = 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
          chartRef.current.style.opacity = '1';
          chartRef.current.style.transform = 'translateY(0)';
        }
      }, 100);
    }
  }, [chartData]);

  const renderTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const dataPoint = payload[0]?.payload as ChartDatum | undefined;
    if (!dataPoint) {
      return null;
    }

    const isAtCrossover = crossoverPoint && dataPoint.monthIndex === crossoverPoint.monthIndex;

    return (
      <div className="rounded-xl border bg-card/95 backdrop-blur-sm p-4 shadow-elegant">
        <div className="font-semibold text-base mb-3 flex items-center">
          <span>{label}</span>
          {isAtCrossover && (
            <Badge className="ml-2 bg-success/20 text-success border-success/30 px-2 py-1">
              <TrendingUp className="h-3 w-3 mr-1" />
              Break-even
            </Badge>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground flex items-center">
              <div className="w-3 h-3 rounded bg-destructive mr-2"></div>
              On-prem cumulative
            </span>
            <span className="font-semibold">{formatCurrency(dataPoint.cumOnPremTcoUSD)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground flex items-center">
              <div className="w-3 h-3 rounded bg-muted-foreground mr-2" style={{ opacity: 0.7 }}></div>
              Cloud cumulative
            </span>
            <span className="font-semibold">{formatCurrency(dataPoint.cumCloudUSD)}</span>
          </div>
          {isAtCrossover && (
            <div className="pt-2 mt-2 border-t border-border">
              <div className="flex justify-between items-center text-success">
                <span className="font-medium">Potential savings:</span>
                <span className="font-bold">{formatCurrency(crossoverPoint.savings)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const maxValue = Math.max(
    ...chartData.map(d => Math.max(d.cumOnPremTcoUSD, d.cumCloudUSD))
  );

  return (
    <div ref={chartRef} className={`relative h-full ${className}`}>
      {/* Crossover indicator */}
      {crossoverPoint && (
        <div className="absolute top-4 right-4 z-10">
          <Badge className="bg-success/20 text-success border-success/30 px-3 py-2 shadow-glow">
            <TrendingUp className="h-4 w-4 mr-2" />
            Break-even: {crossoverPoint.month}
          </Badge>
        </div>
      )}

      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 60, bottom: 5 }}>
          <defs>
            <linearGradient id="onPremGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0.05}/>
            </linearGradient>
            <linearGradient id="cloudGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.2}/>
              <stop offset="95%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.05}/>
            </linearGradient>
          </defs>
          
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="hsl(var(--border))" 
            strokeOpacity={0.3}
          />
          
          <XAxis 
            dataKey="month" 
            stroke="hsl(var(--muted-foreground))" 
            fontSize={12}
            tick={{ fill: 'hsl(var(--muted-foreground))' }}
          />
          
          <YAxis 
            tickFormatter={value => `$${(value / 1000).toFixed(0)}k`}
            stroke="hsl(var(--muted-foreground))" 
            fontSize={12}
            tick={{ fill: 'hsl(var(--muted-foreground))' }}
          />
          
          <Tooltip content={renderTooltip} />
          
          <Legend 
            wrapperStyle={{ 
              paddingTop: '20px',
              fontSize: '14px'
            }}
          />

          {/* Crossover reference area */}
          {crossoverPoint && (
            <>
              <ReferenceLine 
                x={crossoverPoint.month} 
                stroke="hsl(var(--success))" 
                strokeDasharray="4 4" 
                strokeWidth={2}
                opacity={0.7}
              />
              <ReferenceArea
                x1={crossoverPoint.month}
                x2={chartData[chartData.length - 1]?.month}
                fill="hsl(var(--success))"
                fillOpacity={0.05}
              />
            </>
          )}
          
          <Line
            type="monotone"
            dataKey="cumOnPremTcoUSD"
            stroke="hsl(var(--destructive))"
            strokeWidth={4}
            name="Cumulative on-prem cost"
            fill="url(#onPremGradient)"
            dot={{ fill: 'hsl(var(--destructive))', strokeWidth: 0, r: 0 }}
            activeDot={{ 
              r: 6, 
              fill: 'hsl(var(--destructive))', 
              stroke: 'hsl(var(--background))',
              strokeWidth: 2
            }}
            animationDuration={2000}
            animationEasing="ease-out"
          />
          
          <Line
            type="monotone"
            dataKey="cumCloudUSD"
            stroke="hsl(var(--muted-foreground))"
            strokeWidth={3}
            strokeDasharray="8 8"
            name="Cumulative cloud cost"
            fill="url(#cloudGradient)"
            dot={{ fill: 'hsl(var(--muted-foreground))', strokeWidth: 0, r: 0 }}
            activeDot={{ 
              r: 5, 
              fill: 'hsl(var(--muted-foreground))', 
              stroke: 'hsl(var(--background))',
              strokeWidth: 2
            }}
            animationDuration={2000}
            animationEasing="ease-out"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};